/**
 * combo-builder.js
 * Free-selection grid version.
 *
 * State is a single Map<productId, { qty, price, compareAtPrice, title,
 * variantTitle, variantId, image }> plus an `activeFilter` string. There
 * is no step/lock state machine anymore — any visible card can be
 * added/incremented/decremented at any time, subject only to the
 * min/max total-quantity gate that previously gated "how many steps
 * are filled."
 *
 * Product data is read directly off each card's data-* attributes
 * (rendered server-side by combo-product-card.liquid) rather than from
 * a separate JSON product list, since every eligible product is always
 * present in the DOM now (filtering just hides/shows, it never
 * re-fetches). Only cross-cutting config (min/max, tier prices,
 * strings) comes from the JSON data script.
 *
 * Scope: everything is looked up from within a single
 * [data-combo-builder] root, so multiple instances on one page would
 * not conflict with each other.
 */
(function () {
  'use strict';

  function initComboBuilder(root) {
    var dataScript = document.getElementById('combo-builder-data-' + root.dataset.sectionId) ||
                      root.querySelector('script[id^="combo-builder-data-"]');
    if (!dataScript) {
      console.error('[combo-builder] data script not found; aborting init.');
      return;
    }

    var config;
    try {
      config = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('[combo-builder] failed to parse data payload', e);
      return;
    }

    var minProducts = config.minProducts || 3;
    var maxProducts = config.maxProducts || 6;
    var tierPrices = config.tierPrices || {};
    var currency = config.currency || '₹';
    var strings = config.strings || {};

    /** Price for a given total-qty count, or null if that count has no configured tier. */
    function tierPriceFor(count) {
      var val = tierPrices[String(count)];
      return (val && Number(val) > 0) ? Number(val) : null;
    }

    /** The highest tier at or below `count` that actually has a price set. */
    function activeTierPriceFor(count) {
      for (var n = count; n >= minProducts; n--) {
        var price = tierPriceFor(n);
        if (price !== null) return { count: n, price: price };
      }
      return null;
    }

    // ---- State -----------------------------------------------------
    var selections = new Map(); // productId -> { qty, price, compareAtPrice, title, variantTitle, variantId, image }
    var activeFilter = 'all';

    // ---- DOM refs ----------------------------------------------------
    var grid = root.querySelector('[data-combo-grid]');
    var cardEls = Array.prototype.slice.call(root.querySelectorAll('[data-combo-card]'));
    var filterBar = root.querySelector('[data-combo-filters]');
    var filterBtns = Array.prototype.slice.call(root.querySelectorAll('[data-combo-filter]'));
    var emptyFilterEl = root.querySelector('[data-combo-empty-filter]');
    var summaryRoot = root.querySelector('[data-combo-summary]');
    var addToCartBtn = root.querySelector('[data-combo-add-to-cart]');
    var summaryRail = root.querySelector('.combo-builder__summary-rail');
    var originalFinalPriceLabel = (function () {
      var el = summaryRoot && summaryRoot.querySelector('[data-combo-summary-final-price-label]');
      return el ? el.textContent : 'You Pay Only';
    })();

    // ---- Helpers -------------------------------------------------------
    function formatPrice(amount) {
      return currency + Number(amount).toFixed(0);
    }

    function totalQty() {
      var total = 0;
      selections.forEach(function (line) { total += line.qty; });
      return total;
    }

    function cardData(cardEl) {
      return {
        id: cardEl.dataset.productId,
        variantId: cardEl.dataset.variantId,
        title: cardEl.dataset.title,
        variantTitle: cardEl.dataset.variantTitle,
        price: Number(cardEl.dataset.price),
        compareAtPrice: Number(cardEl.dataset.compareAtPrice) || 0,
        image: cardEl.dataset.image,
        available: cardEl.dataset.available === 'true'
      };
    }

    function cardById(productId) {
      return cardEls.filter(function (el) { return el.dataset.productId === productId; })[0];
    }

    // ---- Rendering / state application ----------------------------------

    /** Updates a single card's DOM to reflect its current qty (0 = unselected/Add view, >0 = stepper view). */
    function renderCard(cardEl) {
      var productId = cardEl.dataset.productId;
      var line = selections.get(productId);
      var qty = line ? line.qty : 0;
      var isMaxed = totalQty() >= maxProducts;

      cardEl.classList.toggle('combo-card--selected', qty > 0);

      var qtyEl = cardEl.querySelector('[data-combo-qty]');
      if (qtyEl) qtyEl.textContent = qty;

      var addBtn = cardEl.querySelector('[data-combo-add-btn]');
      if (addBtn) {
        addBtn.disabled = cardEl.dataset.available === 'false' || isMaxed;
      }

      var incBtn = cardEl.querySelector('[data-combo-increment]');
      if (incBtn) incBtn.disabled = isMaxed;

      var decBtn = cardEl.querySelector('[data-combo-decrement]');
      if (decBtn) decBtn.disabled = qty <= 0;
    }

    function renderAllCards() {
      cardEls.forEach(renderCard);
    }

    function renderFilterVisibility() {
      var anyVisible = false;
      cardEls.forEach(function (cardEl) {
        var categories = (cardEl.dataset.comboCategory || '').split(' ');
        var visible = activeFilter === 'all' || categories.indexOf(activeFilter) !== -1;
        cardEl.hidden = !visible;
        if (visible) anyVisible = true;
      });
      if (emptyFilterEl) emptyFilterEl.hidden = anyVisible;
    }

    function setFilter(filter) {
      activeFilter = filter;
      filterBtns.forEach(function (btn) {
        var isActive = btn.dataset.comboFilter === filter;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      renderFilterVisibility();
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }

    /**
     * Uses MRP (compare-at price) for the subtotal/discount math when a
     * product has one set, so "Total MRP" / "Combo Discount" reflect the
     * real saving vs. sticker price — not just vs. the already-discounted
     * selling price. Falls back to the selling price otherwise.
     */
    function mrpUnitFor(line) {
      var hasMrp = line.compareAtPrice && line.compareAtPrice > line.price;
      return (hasMrp ? line.compareAtPrice : line.price) / 100;
    }

    function renderSummary() {
      if (!summaryRoot) return;

      var count = totalQty();
      var countEl = summaryRoot.querySelector('[data-combo-summary-count]');
      if (countEl) countEl.textContent = count;

      var activeTier = count >= minProducts ? activeTierPriceFor(count) : null;

      var banner = summaryRoot.querySelector('[data-combo-summary-unlocked-banner]');
      var helperEl = summaryRoot.querySelector('[data-combo-summary-helper]');

      if (banner) banner.hidden = !activeTier;

      if (helperEl) {
        if (count < minProducts) {
          var stillNeeded = minProducts - count;
          helperEl.hidden = false;
          helperEl.textContent = (strings.addMoreToUnlock || 'Add {n} more to unlock combo pricing').replace('{n}', stillNeeded);
        } else if (count < maxProducts) {
          var nextTierPrice = tierPriceFor(count + 1);
          if (nextTierPrice !== null) {
            helperEl.hidden = false;
            helperEl.textContent = (strings.addOneMoreFor || 'Add 1 more for {price}!').replace('{price}', formatPrice(nextTierPrice));
          } else {
            helperEl.hidden = true;
            helperEl.textContent = '';
          }
        } else {
          helperEl.hidden = false;
          helperEl.textContent = strings.maxReached || 'Maximum reached!';
        }
      }

      // Running line-item list
      var rowsContainer = summaryRoot.querySelector('[data-combo-summary-rows]');
      var emptyEl = summaryRoot.querySelector('[data-combo-summary-empty]');
      if (rowsContainer) {
        rowsContainer.innerHTML = '';
        selections.forEach(function (line, productId) {
          var row = document.createElement('div');
          row.className = 'combo-summary-row';
          row.dataset.comboSummaryProduct = productId;
          row.innerHTML =
            '<img class="combo-summary-row__thumb" src="' + line.image + '" alt="" loading="lazy">' +
            '<span class="combo-summary-row__title">' + escapeHtml(line.title) +
              (line.variantTitle && line.variantTitle !== 'Default Title' ? '<br><small>' + escapeHtml(line.variantTitle) + '</small>' : '') +
              (line.qty > 1 ? '<br><small>Qty ' + line.qty + '</small>' : '') +
            '</span>' +
            '<span class="combo-summary-row__price">' + formatPrice((line.price * line.qty) / 100) + '</span>' +
            '<button type="button" class="combo-summary-row__remove" data-combo-remove-product="' + productId + '" aria-label="' + escapeHtml((strings.remove || 'Remove') + ' ' + line.title) + '">&times;</button>';
          rowsContainer.appendChild(row);
        });
      }
      if (emptyEl) emptyEl.hidden = selections.size > 0;

      var subtotal = 0;
      selections.forEach(function (line) { subtotal += mrpUnitFor(line) * line.qty; });
      var discount = activeTier ? Math.max(subtotal - activeTier.price, 0) : 0;
      var discountPercent = subtotal > 0 ? Math.round((discount / subtotal) * 100) : 0;

      var finalPriceLabel = summaryRoot.querySelector('[data-combo-summary-final-price-label]');
      var finalPriceEl = summaryRoot.querySelector('[data-combo-summary-final-price]');
      if (activeTier) {
        if (finalPriceLabel) finalPriceLabel.textContent = originalFinalPriceLabel;
        if (finalPriceEl) finalPriceEl.textContent = formatPrice(activeTier.price);
      } else {
        if (finalPriceLabel) finalPriceLabel.textContent = strings.selectAtLeast
          ? strings.selectAtLeast.replace('{n}', minProducts)
          : ('Select at least ' + minProducts + ' products');
        if (finalPriceEl) finalPriceEl.textContent = '\u2014';
      }

      var saveLine = summaryRoot.querySelector('[data-combo-summary-save-line]');
      if (saveLine) {
        if (discount > 0) {
          saveLine.hidden = false;
          saveLine.textContent = (strings.youSave || 'You Save {amount} ({percent}%)')
            .replace('{amount}', formatPrice(discount))
            .replace('{percent}', discountPercent);
        } else {
          saveLine.hidden = true;
        }
      }

      var freeTag = summaryRoot.querySelector('[data-combo-summary-free-tag]');
      if (freeTag) freeTag.hidden = discount <= 0;

      var subtotalEl = summaryRoot.querySelector('[data-combo-summary-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formatPrice(subtotal);
      var discountEl = summaryRoot.querySelector('[data-combo-summary-discount]');
      if (discountEl) discountEl.textContent = '-' + formatPrice(discount);

      if (addToCartBtn) {
        addToCartBtn.disabled = !activeTier;
        var addLabelEl = addToCartBtn.querySelector('[data-combo-add-to-cart-label]');
        if (addLabelEl) {
          addLabelEl.textContent = activeTier
            ? (strings.addToCart || 'Add Combo to Cart') + ' \u2022 ' + formatPrice(activeTier.price)
            : (strings.addToCart || 'Add Combo to Cart');
        }
      }
    }

    function renderAll() {
      renderAllCards();
      renderSummary();
    }

    // ---- Mutations -----------------------------------------------------
    function addProduct(cardEl) {
      if (cardEl.dataset.available === 'false') {
        if (strings.outOfStock) alert(strings.outOfStock);
        return;
      }
      if (totalQty() >= maxProducts) return; // capped at max
      var data = cardData(cardEl);
      selections.set(data.id, {
        qty: 1,
        price: data.price,
        compareAtPrice: data.compareAtPrice,
        title: data.title,
        variantTitle: data.variantTitle,
        variantId: data.variantId,
        image: data.image
      });
      renderAll();
    }

    function changeQty(productId, delta) {
      var line = selections.get(productId);
      if (!line) return;
      var newQty = line.qty + delta;
      if (delta > 0 && totalQty() >= maxProducts) return; // capped at max
      if (newQty <= 0) {
        selections.delete(productId);
      } else {
        line.qty = newQty;
      }
      renderAll();
    }

    function removeProduct(productId) {
      selections.delete(productId);
      renderAll();
    }

    // ---- Event handling --------------------------------------------------
    root.addEventListener('click', function (evt) {
      var filterBtn = evt.target.closest('[data-combo-filter]');
      if (filterBtn) return setFilter(filterBtn.dataset.comboFilter);

      var addBtn = evt.target.closest('[data-combo-add-btn]');
      if (addBtn) {
        var cardEl = addBtn.closest('[data-combo-card]');
        if (cardEl) return addProduct(cardEl);
      }

      var incBtn = evt.target.closest('[data-combo-increment]');
      if (incBtn) {
        var incCard = incBtn.closest('[data-combo-card]');
        if (incCard) return changeQty(incCard.dataset.productId, 1);
      }

      var decBtn = evt.target.closest('[data-combo-decrement]');
      if (decBtn) {
        var decCard = decBtn.closest('[data-combo-card]');
        if (decCard) return changeQty(decCard.dataset.productId, -1);
      }

      var wishlistBtn = evt.target.closest('[data-combo-wishlist-btn]');
      if (wishlistBtn) {
        // Visual-only toggle (no persistence / real wishlist wiring) —
        // see project notes for what would be needed to make this real.
        var pressed = wishlistBtn.getAttribute('aria-pressed') === 'true';
        wishlistBtn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
        wishlistBtn.classList.toggle('is-active', !pressed);
        return;
      }

      var removeBtn = evt.target.closest('[data-combo-remove-product]');
      if (removeBtn) return removeProduct(removeBtn.dataset.comboRemoveProduct);

      var addToCart = evt.target.closest('[data-combo-add-to-cart]');
      if (addToCart) return handleAddToCart(addToCart);
    });

    async function handleAddToCart(btn) {
      if (btn.disabled) return;
      var count = totalQty();
      if (count < minProducts) return; // defensive re-check, never trust UI state alone
      var activeTier = activeTierPriceFor(count);
      if (!activeTier) return; // no configured price for this count

      // Expand qty > 1 into repeated {variantId, price} entries rather
      // than passing a `quantity` field — combo-builder-cart.js always
      // adds each array entry at quantity 1, and /cart/add.js merges
      // repeated identical variant ids into one line with the summed
      // quantity. This keeps combo-builder-cart.js's internals untouched.
      var items = [];
      selections.forEach(function (line) {
        for (var i = 0; i < line.qty; i++) {
          items.push({ variantId: line.variantId, price: line.price });
        }
      });

      // Fresh per click — never reuse across separate "Add to Cart"
      // presses in the same page view, or two combos built back-to-back
      // would get merged into one group by the discount Function.
      var comboId = 'combo_' + config.sectionId + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

      btn.disabled = true;
      btn.classList.add('is-loading');
      var originalLabel = btn.innerHTML;
      btn.textContent = 'Adding\u2026';
      announceStatus('Adding your combo to the cart\u2026');

      try {
        await window.ComboBuilderCart.addComboToCart(items, {
          comboId: comboId,
          comboOfferName: 'Buy ' + count + ' for ' + formatPrice(activeTier.price)
        });
        btn.textContent = 'Added \u2713';
        announceStatus('Your combo has been added to the cart.');
        if (window.ComboBuilderConfetti) window.ComboBuilderConfetti.burst(btn);
        setTimeout(function () {
          btn.innerHTML = originalLabel;
          btn.classList.remove('is-loading');
          btn.disabled = !activeTierPriceFor(totalQty());
        }, 1800);
      } catch (err) {
        console.error('[combo-builder] add to cart failed', err);
        btn.innerHTML = originalLabel;
        btn.classList.remove('is-loading');
        btn.disabled = !activeTierPriceFor(totalQty());
        var errorMsg = strings.errorAddToCart || 'Something went wrong adding your combo to the cart. Please try again.';
        announceStatus(errorMsg);
        alert(errorMsg);
      }
    }

    /** Updates the aria-live status region so screen reader users (and
     *  anything else reading ARIA live regions, including automated
     *  agents) get the same confirmation sighted users see visually —
     *  independent of the confetti effect, which is purely decorative. */
    function announceStatus(message) {
      var statusEl = root.querySelector('[data-combo-status]');
      if (statusEl) statusEl.textContent = message;
    }

    // ---- Mobile bottom-sticky summary toggle ------------------------------
    // Always docked at the bottom on mobile (matches the mobile reference
    // design's persistent bar), rather than only appearing after scrolling
    // past the inline panel. Desktop (>=990px) never runs this block, so
    // the desktop sidebar layout/behavior is completely unaffected.
    if (summaryRail && window.matchMedia('(max-width: 989px)').matches) {
      summaryRail.classList.add('is-sticky-bottom');
    }

    setFilter('all');
    renderAll();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-combo-builder]');
    roots.forEach(function (root) {
      if (!root.dataset.sectionId) {
        var dataScript = root.querySelector('script[id^="combo-builder-data-"]');
        if (dataScript) root.dataset.sectionId = dataScript.id.replace('combo-builder-data-', '');
      }
      initComboBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var root = evt.target.querySelector('[data-combo-builder]');
    if (root) initComboBuilder(root);
  });
})();